import React, { useEffect, useMemo, useState } from "react";
import type { CampaignSummary } from "../types";

export interface DuplicateCampaignPayload {
  sourceCampaignId: string;
  name: string;
  sections: {
    sequence: boolean;
    characterSelection: boolean;
    replyTiming: boolean;
    noChatIntro: boolean;
    premiumReachouts: boolean;
  };
}

interface DuplicateCampaignModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (payload: DuplicateCampaignPayload) => void | Promise<void>;
  campaignOptions: CampaignSummary[];
  defaultSourceCampaignId?: string;
  defaultName?: string;
  isSubmitting?: boolean;
}

const SECTION_LABELS: Array<{
  key: keyof DuplicateCampaignPayload["sections"];
  label: string;
  helper: string;
}> = [
  {
    key: "sequence",
    label: "Re-engagement sequence",
    helper: "Steps, offsets, and prompts.",
  },
  {
    key: "characterSelection",
    label: "Character selection",
    helper: "Casting choices and thresholds.",
  },
  {
    key: "replyTiming",
    label: "Reply timing",
    helper: "Dynamic ranges and delays.",
  },
  {
    key: "noChatIntro",
    label: "No-chat intro",
    helper: "Schedule, triggers, and content.",
  },
  {
    key: "premiumReachouts",
    label: "Reach-outs",
    helper: "Rules, prompts, and manual emails.",
  },
];

export const DuplicateCampaignModal: React.FC<DuplicateCampaignModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  campaignOptions,
  defaultSourceCampaignId,
  defaultName,
  isSubmitting = false,
}) => {
  const [nameInput, setNameInput] = useState("");
  const [sourceCampaignId, setSourceCampaignId] = useState("");
  const [sections, setSections] = useState<
    DuplicateCampaignPayload["sections"]
  >({
    sequence: true,
    characterSelection: true,
    replyTiming: true,
    noChatIntro: true,
    premiumReachouts: true,
  });
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setNameInput(defaultName ?? "");
    setSourceCampaignId(defaultSourceCampaignId ?? campaignOptions[0]?.id ?? "");
    setSections({
      sequence: true,
      characterSelection: true,
      replyTiming: true,
      noChatIntro: true,
      premiumReachouts: true,
    });
    setError(null);
  }, [isOpen, defaultName, defaultSourceCampaignId, campaignOptions]);

  const allChecked = useMemo(
    () => Object.values(sections).every(Boolean),
    [sections]
  );

  const toggleAll = () => {
    const next = !allChecked;
    setError(null);
    setSections({
      sequence: next,
      characterSelection: next,
      replyTiming: next,
      noChatIntro: next,
      premiumReachouts: next,
    });
  };

  const toggleSection = (key: keyof DuplicateCampaignPayload["sections"]) => {
    setError(null);
    setSections((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const handleSubmit = () => {
    const trimmedName = nameInput.trim();
    if (!sourceCampaignId) {
      setError("Pick a campaign to copy from.");
      return;
    }
    if (!trimmedName) {
      setError("Give the new campaign a name.");
      return;
    }
    if (!Object.values(sections).some(Boolean)) {
      setError("Select at least one section to copy.");
      return;
    }
    setError(null);
    onConfirm({
      sourceCampaignId,
      name: trimmedName,
      sections,
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-gray-900/40 backdrop-blur-sm"
        onClick={onClose}
      ></div>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative w-full max-w-3xl overflow-hidden rounded-3xl bg-white shadow-2xl">
          <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
            <div>
              <div className="text-lg font-semibold text-gray-900">
                Duplicate campaign
              </div>
            <p className="text-xs text-gray-500">
              Clone an existing campaign and choose which parts to bring over.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-gray-200 p-2 text-gray-500 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
            aria-label="Close duplicate campaign modal"
          >
            <svg
              className="h-4 w-4"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m6 6 8 8M14 6l-8 8"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className="max-h-[70vh] overflow-y-auto px-6 py-5 space-y-5">
          <div className="space-y-2 rounded-2xl border border-gray-100 bg-gray-50/70 p-4">
            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                Copy from
              </span>
              <select
                value={sourceCampaignId}
                onChange={(event) => {
                  setSourceCampaignId(event.target.value);
                  setError(null);
                }}
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-800 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              >
                {campaignOptions.map((campaign) => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                    {campaign.status === "active" ? " (Live)" : ""}
                  </option>
                ))}
              </select>
            </label>

            <label className="flex flex-col gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                New campaign name
              </span>
              <input
                type="text"
                value={nameInput}
                onChange={(event) => {
                  setNameInput(event.target.value);
                  setError(null);
                }}
                placeholder="e.g., Summer Re-engagement Variant B"
                className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[var(--primary-color)]"
              />
            </label>
          </div>

          <div className="space-y-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="text-sm font-semibold text-gray-900">
                  What should we copy?
                </div>
                <p className="text-xs text-gray-500">
                  Pick the blocks to duplicate so you can remix the rest later.
                </p>
              </div>
              <button
                type="button"
                onClick={toggleAll}
                className="rounded-full border border-gray-200 px-3 py-1.5 text-xs font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
              >
                {allChecked ? "Deselect all" : "Select all"}
              </button>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              {SECTION_LABELS.map((section) => {
                const isChecked = sections[section.key];
                return (
                  <label
                    key={section.key}
                    className={`relative flex cursor-pointer gap-3 rounded-2xl border px-4 py-3 transition ${
                      isChecked
                        ? "border-[var(--primary-color)] bg-[var(--primary-color)]/5 shadow-sm"
                        : "border-gray-200 hover:border-[var(--primary-color)]/60"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggleSection(section.key)}
                      className="mt-1 h-4 w-4 cursor-pointer accent-[var(--primary-color)]"
                    />
                    <div className="space-y-1">
                      <div className="text-sm font-semibold text-gray-900">
                        {section.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {section.helper}
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
          </div>

          {error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          ) : null}
        </div>

        <div className="flex items-center justify-between border-t border-gray-100 px-6 py-4">
          <div className="text-xs text-gray-500">
            You can tweak copied steps before activating the new campaign.
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-gray-600 transition hover:border-[var(--primary-color)] hover:text-[var(--primary-color)]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="rounded-full bg-[var(--primary-color)] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:shadow disabled:opacity-60"
            >
              {isSubmitting ? "Duplicating..." : "Duplicate campaign"}
            </button>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};
